/**
 * Build render segments for an announcement sentence with optional inline links.
 * linkText empty => whole sentence is one link (uses that link's url).
 * No dangerouslySetInnerHTML — returns [{ type: 'text'|'link', value, url? }].
 */
export function buildAnnouncementSegments(text, links = []) {
    const source = String(text || '');
    if (!source) return [];

    const list = Array.isArray(links) ? links : [];
    const whole = list.find((l) => l && !(l.linkText || l.link_text || '').trim() && (l.url || '').trim());
    if (whole) {
        return [{ type: 'link', value: source, url: String(whole.url).trim() }];
    }

    const partials = list
        .map((l) => ({
            linkText: String(l?.linkText || l?.link_text || '').trim(),
            url: String(l?.url || '').trim()
        }))
        .filter((l) => l.linkText && l.url && source.includes(l.linkText));

    if (partials.length === 0) {
        return [{ type: 'text', value: source }];
    }

    // Find non-overlapping matches by earliest index, longer linkText first on ties
    const used = [];
    const matches = [];
    const sorted = [...partials].sort((a, b) => b.linkText.length - a.linkText.length);

    for (const p of sorted) {
        let from = 0;
        while (from < source.length) {
            const idx = source.indexOf(p.linkText, from);
            if (idx === -1) break;
            const end = idx + p.linkText.length;
            const overlaps = used.some((r) => !(end <= r.start || idx >= r.end));
            if (!overlaps) {
                used.push({ start: idx, end });
                matches.push({ start: idx, end, url: p.url, value: p.linkText });
                break;
            }
            from = idx + 1;
        }
    }

    matches.sort((a, b) => a.start - b.start);

    const segments = [];
    let cursor = 0;
    for (const m of matches) {
        if (m.start > cursor) {
            segments.push({ type: 'text', value: source.slice(cursor, m.start) });
        }
        segments.push({ type: 'link', value: m.value, url: m.url });
        cursor = m.end;
    }
    if (cursor < source.length) {
        segments.push({ type: 'text', value: source.slice(cursor) });
    }
    return segments;
}

export function isNewsletterUrl(url) {
    return String(url || '').trim() === 'newsletter';
}

export function isExternalAnnouncementUrl(url) {
    const u = String(url || '').trim();
    return /^https?:\/\//i.test(u) || /^mailto:/i.test(u);
}
